import { detailAggregateDef, detailAggregateLayout, detailAggregateOf, usesDetailAggregates } from "./detailAggregates";
import { contextWithCalculatedColumns } from "./calculatedColumns";
import type {
    CategorizationRule,
    DataModel,
    MetricSection,
    ModelKey,
    OtpRule,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
    RuleCondition,
    RuleNode,
    RuleSet,
} from "../types";
import type { ReportLogicContext } from "./context";
import {
    TRIP_DATA_FIELDS,
    breakdownColumnForModel,
    detailColumnsForModel,
    findReportColumn,
    findSelectedDetailColumn,
    findTimestampColumn,
    targetFieldDef,
} from "./catalogResolution";
import { resolveLookupOptionsForColumn } from "./lookupResolution";
import { ruleOpDef, ruleOperatorSentence } from "./operatorCatalog";
import { effectiveComparisonTier, resolveDifferenceUnit } from "./temporalPrecision";
import { nullFallbackFor, todayPhrase } from "./sqlExpressions";
import { isRuleGroupNode, ruleNodeJoin } from "./rules";
import { ruleResultLabel, ruleResultValue, ruleValueFromColumn } from "./ruleInterpretation";
import { detailConstantColumns, detailConstantText } from "./detailConstantColumns";
import { calculatedFieldEnglish, calculatedFields, detailCalculatedColumns, findCalculatedField, measureAggregateDef, usesMeasureField } from "./calculatedFields";
import { formatReportDate, granularityLabel, isCustomReportingPeriod, isLastNReportingPeriod, periodPlainEnglish } from "./reportingPeriods";
import { reportGlobalFilters } from "./metrics";
import { isPeriodDateExtra, periodDateNarrative } from "./periodDate";
import { metricOverridesReportPeriod, metricPeriodFilterOutline, modelAppliesReportingPeriod } from "./periodBounds";
import { combinedRowLabel, combinedRows, combinedRuleSetIds, usesCombinedRuleSets } from "./combinedRuleSetSelection";

// =====================================================================================
// The narrative layer: one place that turns a saved configuration into readable English.
//
// WHY THIS EXISTS. Before this module the same configuration was described three times in
// three voices - a run-on paragraph in summaries.ts that was saved but never shown, nested
// bullet lists in the preview's Summary tab, and token rows in its Rule Details tab. They
// drifted, because nothing forced them to agree: a field added to one was simply missing
// from the others. Everything that puts this configuration into words now goes through the
// functions below, and the renderers only decide how to lay the result out.
//
// WHAT IT MAY AND MAY NOT DO. It describes; it never decides. Every fact it states is read
// from the same helpers the SQL builders read (metricFilterNodes, breakdownColumnForModel,
// the operator catalog, the lookup catalog), never paraphrased from a second reading of the
// config. That constraint is the whole point: prose reads as authoritative whether or not
// it is true, so a rule book that derived its own view of the rules would eventually
// describe a query the report does not run - which is worse than showing nothing. If a
// sentence here cannot be traced to the builders, it does not belong in this file.
//
// It stays pure: no React, no DOM, no formatting decisions beyond sentence construction.
// =====================================================================================

/** A condition tree flattened into language, keeping the shape so it can also be indented. */
export type NarrativeClause =
    | { kind: "condition"; text: string }
    | { kind: "group"; join: "and" | "or"; clauses: NarrativeClause[] };

export interface NarrativeBlock {
    id: string;
    title: string;
    /** Short qualifier under the title - the data model, the metric type, a count. */
    subtitle?: string;
    paragraphs: string[];
    /** Rendered as an indented list where a sentence would be too dense to follow. */
    outline?: NarrativeClause | null;
}

export interface NarrativeSection {
    id: string;
    heading: string;
    /** One sentence that stands on its own if the reader skims only the headings. */
    lead?: string;
    paragraphs: string[];
    blocks: NarrativeBlock[];
}

export interface RuleBook {
    title: string;
    subtitle: string;
    sections: NarrativeSection[];
}

const NOT_SET = "not set";

// --- sentence plumbing ---------------------------------------------------------------

/** "A", "A and B", "A, B and C" - never an Oxford comma, so it reads as speech. */
function joinList(items: string[], conjunction: "and" | "or" = "and") {
    const clean = items.filter((item) => !!item && item.trim());
    if (!clean.length) return "";
    if (clean.length === 1) return clean[0];
    return `${clean.slice(0, -1).join(", ")} ${conjunction} ${clean[clean.length - 1]}`;
}

function sentence(text: string) {
    const trimmed = String(text || "").trim();
    if (!trimmed) return "";
    const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

function plural(count: number, one: string, many: string) {
    return count === 1 ? one : many;
}

/**
 * The reporting period as it reads mid-sentence. periodPlainEnglish returns either a named
 * period ("previous month") or an already-complete phrase for a custom range ("between X
 * and Y") - only the first kind wants an article in front of it.
 */
function periodPhrase(report: ReportConfig) {
    const period = periodPlainEnglish(report);
    return isCustomReportingPeriod(report.reportingPeriod.type) || isLastNReportingPeriod(report.reportingPeriod.type) ? period : `the ${period}`;
}

// --- catalog-aware wording -----------------------------------------------------------

/**
 * A stored value as a person would recognise it. Report values are stored as the codes the
 * source system uses; the editor shows the lookup name beside them, and a rule book that
 * printed the code would be describing rules in a vocabulary its reader does not have. An
 * unmatched value is returned unchanged rather than hidden - a value with no lookup row is
 * still a value the query filters on.
 */
function valueLabel(context: ReportLogicContext, modelKey: ModelKey | "", columnKey: string, value: string) {
    if (!value) return NOT_SET;
    const options: ReportConfigurationLookupOption[] = resolveLookupOptionsForColumn(context, modelKey, columnKey);
    return options.find((option) => option.value === value)?.name || value;
}

function valueLabels(context: ReportLogicContext, modelKey: ModelKey | "", columnKey: string, values: string[]) {
    return values.map((value) => valueLabel(context, modelKey, columnKey, value));
}

function columnPhrase(context: ReportLogicContext, report: ReportConfig, columnKey: string) {
    const column = findReportColumn(context, report, columnKey);
    return column?.label || columnKey || "a field";
}

function modelPhrase(model?: DataModel | null) {
    return model?.label ? `${model.label.toLowerCase()} records` : "records";
}

// --- conditions ----------------------------------------------------------------------

/** One leaf condition as a clause: "pickup delay is more than 15". */
function describeCondition(context: ReportLogicContext, report: ReportConfig, condition: RuleCondition): NarrativeClause {
    const column = findReportColumn(context, report, condition.column);
    const field = column?.label || condition.column || "a field";
    const operator = ruleOpDef(condition.operator);
    const words = ruleOperatorSentence(condition.operator);

    // Operand modes read as sentences of their own: the right-hand side is a field name, not
    // a value, so it must not be quoted or run through valueLabels.
    const compareField = () => {
        const other = condition.compareColumn ? findReportColumn(context, report, condition.compareColumn) : null;
        return other?.label || condition.compareColumn || "a field";
    };
    const otherColumn = condition.compareColumn ? findReportColumn(context, report, condition.compareColumn) : null;
    if (condition.leftKind === "minutes_between") {
        const unit = resolveDifferenceUnit(column, otherColumn, condition.differenceUnit);
        const unitWord = unit === "minute" ? "minutes" : unit === "hour" ? "hours" : "days";
        const subject = condition.absoluteMinutes
            ? `${unitWord} apart ${field} and ${compareField()} are`
            : `${unitWord} from ${field} to ${compareField()}`;
        if (operator.valueMode === "range") {
            return { kind: "condition", text: `the ${subject} ${words} ${condition.values[0] || NOT_SET} and ${condition.values[1] || NOT_SET}` };
        }
        return { kind: "condition", text: `the ${subject} ${words} ${condition.values[0] || NOT_SET}` };
    }
    // "If blank, use <value>" reads as part of the subject: "end date (or 12-31-2199 when blank)".
    // subject === field whenever no fallback applies, so every sentence reads as before.
    const fallback = nullFallbackFor(condition, column);
    const subject = fallback ? `${field} (or ${fallback.display} when blank)` : field;
    if (condition.compareTo === "today") {
        return { kind: "condition", text: `${subject} ${todayPhrase(condition.operator, "is")}` };
    }
    if (condition.compareTo === "column") {
        const tier = effectiveComparisonTier(column, otherColumn, condition.compareGranularity);
        const suffix = tier ? `, compared by ${tier}` : "";
        return { kind: "condition", text: `${subject} ${words} ${compareField()}${suffix}` };
    }
    if (operator.valueMode === "none") return { kind: "condition", text: `${field} ${words}` };

    if (operator.valueMode === "text") {
        const text = condition.text.trim();
        return { kind: "condition", text: `${subject} ${words} "${text || NOT_SET}"` };
    }

    const labels = valueLabels(context, column?.model || "", condition.column, condition.values);

    if (operator.valueMode === "range") {
        return { kind: "condition", text: `${subject} ${words} ${labels[0] || NOT_SET} and ${labels[1] || NOT_SET}` };
    }

    if (operator.valueMode === "single") {
        return { kind: "condition", text: `${subject} ${words} ${labels[0] || NOT_SET}` };
    }

    return { kind: "condition", text: `${subject} ${words} ${joinList(labels, "or") || NOT_SET}` };
}

/**
 * A whole condition tree as clauses.
 *
 * Grouping follows ruleNodeJoin and the AND-binds-tighter-than-OR rule the SQL builders
 * use, rather than the order the conditions happen to be stored in - the reader has to end
 * up with the same precedence the query has. Runs of AND-joined siblings become one group,
 * and those groups are joined with OR.
 */
function describeConditionTree(context: ReportLogicContext, report: ReportConfig, nodes: RuleNode[], logic: "and" | "or"): NarrativeClause | null {
    if (!nodes || !nodes.length) return null;

    const runs: RuleNode[][] = [];
    nodes.forEach((node, index) => {
        const join = index === 0 ? "and" : ruleNodeJoin(node, logic);
        if (!runs.length || join === "or") runs.push([node]);
        else runs[runs.length - 1].push(node);
    });

    const clauseFor = (node: RuleNode): NarrativeClause | null => (isRuleGroupNode(node)
        ? describeConditionTree(context, report, node.conditions, node.conditionLogic)
        : describeCondition(context, report, node as RuleCondition));

    const runClauses = runs
        .map((run) => {
            const clauses = run.map(clauseFor).filter(Boolean) as NarrativeClause[];
            if (!clauses.length) return null;
            return clauses.length === 1 ? clauses[0] : { kind: "group" as const, join: "and" as const, clauses };
        })
        .filter(Boolean) as NarrativeClause[];

    if (!runClauses.length) return null;
    return runClauses.length === 1 ? runClauses[0] : { kind: "group", join: "or", clauses: runClauses };
}

/** How deeply a clause nests - the renderer uses this to decide sentence versus outline. */
function clauseDepth(clause: NarrativeClause | null): number {
    if (!clause) return 0;
    if (clause.kind === "condition") return 1;
    return 1 + Math.max(0, ...clause.clauses.map(clauseDepth));
}

/** A clause as one sentence fragment, parenthesised only where precedence needs it. */
function clauseText(clause: NarrativeClause | null, depth = 0): string {
    if (!clause) return "";
    if (clause.kind === "condition") return clause.text;
    const inner = clause.clauses.map((child) => clauseText(child, depth + 1)).filter(Boolean).join(` ${clause.join} `);
    return depth > 0 ? `(${inner})` : inner;
}

function countConditions(nodes: RuleNode[]): number {
    return (nodes || []).reduce((count, node) => count + (isRuleGroupNode(node) ? countConditions(node.conditions) : 1), 0);
}

// --- metrics -------------------------------------------------------------------------

function metricFilterClause(context: ReportLogicContext, report: ReportConfig, section: MetricSection, mode: "filter" | "excludeFilter") {
    const stored = mode === "filter" ? section.filterConditions : section.excludeFilterConditions;
    const legacyColumn = mode === "filter" ? section.filterColumn : section.excludeFilterColumn;
    const legacyValues = mode === "filter" ? section.filterValues : section.excludeFilterValues;
    const storedLogic = mode === "filter" ? section.filterLogic : section.excludeFilterLogic;
    const logic: "and" | "or" = storedLogic === "or" ? "or" : storedLogic === "and" ? "and" : mode === "filter" ? "and" : "or";

    if (stored && stored.length) return describeConditionTree(context, report, stored, logic);

    // A config written before the condition tree existed still carries the single-column
    // pair, and metricFilterNodes synthesises the same one-condition tree from it at query
    // time - so the rule book has to describe it too, or an older report would read as
    // having no filters at all.
    if (!legacyColumn || !legacyValues?.length) return null;
    const field = columnPhrase(context, report, legacyColumn);
    const column = findReportColumn(context, report, legacyColumn);
    const labels = valueLabels(context, column?.model || section.model, legacyColumn, legacyValues);
    return {
        kind: "condition" as const,
        text: `${field} ${mode === "filter" ? "is one of" : "is not one of"} ${joinList(labels, "or")}`,
    };
}

function coPlanNames(section: MetricSection, clientOptions?: ReportConfigurationLookupOption[]) {
    return (section.coPlanClientIds || [])
        .filter((id) => Number.isInteger(id) && id > 0)
        .map((id) => clientOptions?.find((option) => option.value === String(id))?.name || `client ${id}`);
}

function otpRuleSentence(context: ReportLogicContext, report: ReportConfig, section: MetricSection, rule: OtpRule, index: number) {
    const model = section.model ? context.dataModels[section.model] : null;
    const compare = model?.timestampColumns.find((column) => column.key === rule.compareCol);
    const against = model?.timestampColumns.find((column) => column.key === rule.againstCol);
    const scope = rule.conditions
        .filter((condition) => condition.column && condition.values.length)
        .map((condition) => `${columnPhrase(context, report, condition.column)} is one of ${joinList(valueLabels(context, section.model, condition.column, condition.values), "or")}`);
    const when = scope.length ? `when ${joinList(scope, rule.conditionLogic === "or" ? "or" : "and")}` : "for every record";
    return sentence(`Rule ${index + 1}: ${when}, a record counts as late if ${compare?.label || NOT_SET} is ${rule.thresholdMinutes} ${plural(rule.thresholdMinutes, "minute", "minutes")} or more away from ${against?.label || NOT_SET}`);
}

function describeMetric(
    context: ReportLogicContext,
    report: ReportConfig,
    section: MetricSection,
    clientOptions?: ReportConfigurationLookupOption[],
): NarrativeBlock {
    const model = section.model ? context.dataModels[section.model] : null;
    const name = section.name || "This metric";
    const paragraphs: string[] = [];

    if (!section.model || !section.breakdownColumn) {
        return {
            id: section.id,
            title: name,
            subtitle: "Not finished",
            paragraphs: [sentence("This metric is not finished yet - it has no column picked, so it produces nothing")],
            outline: null,
        };
    }

    const breakdownColumn = breakdownColumnForModel(context, section.model, section.breakdownColumn);
    const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
    const breakdownName = section.breakdownSource === "ruleSet"
        ? (ruleSet ? `the ${ruleSet.name || "categorisation"} categories` : "categories from a rule set that has not been chosen yet")
        : (breakdownColumn?.label || section.breakdownColumn).toLowerCase();
    const basedOn = findTimestampColumn(model || undefined, report.modelBasedOn[section.model]) || model?.timestampColumns[0];
    const dateWords = basedOn?.label ? `by ${basedOn.label.toLowerCase()}` : "by a date field that has not been chosen yet";
    const counted = section.countMode === "distinct" && section.countDistinctColumn
        ? `distinct ${columnPhrase(context, report, section.countDistinctColumn).toLowerCase()} values`
        : modelPhrase(model);

    // The headline sentence: what the metric produces, in the reader's terms.
    if (section.displayAs === "detail") {
        const columns = section.selectedColumns
            .map((key) => findSelectedDetailColumn(detailColumnsForModel(context, section.model), key)?.label || key);
        paragraphs.push(sentence(columns.length
            ? `Lists every matching ${model?.label.toLowerCase() || "record"}, showing ${joinList(columns)}`
            : `Lists every matching ${model?.label.toLowerCase() || "record"} with all of its columns`));
        const calcColumns = detailCalculatedColumns(context, report, section).map((item) => item.label);
        if (calcColumns.length) paragraphs.push(sentence(`It also works out ${joinList(calcColumns)} for each row (see Calculated fields)`));
        const fixedColumns = detailConstantColumns(section).map(detailConstantText);
        if (fixedColumns.length) paragraphs.push(sentence(`Every row also carries ${plural(fixedColumns.length, "a fixed column", "fixed columns")}: ${joinList(fixedColumns)}`));
        // Aggregated sheet columns (logic/detailAggregates.ts) group the rows.
        const aggregateLayout = detailAggregateLayout(section);
        if (aggregateLayout && usesDetailAggregates(section)) {
            const onSheet = aggregateLayout.columns.filter((column) => column.detail?.include ?? true);
            const name = (column: (typeof onSheet)[number]) => column.detail?.header?.trim() || column.label;
            const groupedBy = onSheet.filter((column) => detailAggregateOf(column) === "none" && !column.sourceAlias.startsWith("detail:const:")).map(name);
            const totals = onSheet.filter((column) => detailAggregateOf(column) !== "none")
                .map((column) => `${detailAggregateDef(detailAggregateOf(column)).label.toLowerCase()} of ${column.label}`);
            paragraphs.push(sentence(groupedBy.length
                ? `On the sheet, rows are grouped by ${joinList(groupedBy)}, showing the ${joinList(totals)} for each group`
                : `On the sheet, all rows are summed up into one line showing the ${joinList(totals)}`));
            if (aggregateLayout.detailTotals?.show) paragraphs.push(sentence("A grand total row follows the groups"));
        }
    } else if (section.displayAs === "otp") {
        paragraphs.push(sentence(section.noBreakdown
            ? `Works out one on-time performance figure across all ${modelPhrase(model)}`
            : `Works out on-time performance for ${modelPhrase(model)}, split by ${breakdownName}`));
    } else if (usesCombinedRuleSets(section)) {
        // Combined categorizations: several rule sets, each applied on its own.
        const sets = combinedRuleSetIds(section).map((id) => report.bucketRuleSets.find((set) => set.id === id)?.name || "a missing rule set");
        const rows = combinedRows(section).map((row) => `${combinedRowLabel(row)}${row.value === "share" ? " (share of its rule set)" : ""}`);
        paragraphs.push(sentence(`Counts ${counted} in ${sets.length} ${plural(sets.length, "rule set", "rule sets")}, each applied on its own: ${joinList(sets)}. A record can be counted once in every rule set`));
        if (rows.length) paragraphs.push(sentence(`It shows ${joinList(rows)}`));
    } else if (usesMeasureField(section)) {
        // Sum / Average / Min / Max of a calculated field instead of a row count.
        const fieldName = findCalculatedField(report, section.measure!.fieldId)?.name || "a calculated field";
        const verb = ({ sum: "Adds up", avg: "Averages", min: "Finds the smallest", max: "Finds the largest" } as const)[measureAggregateDef(section).key];
        paragraphs.push(sentence(section.displayAs === "percentage"
            ? `${verb} ${fieldName} across ${modelPhrase(model)} and shows each ${breakdownName}'s share of the total`
            : `${verb} ${fieldName} across ${modelPhrase(model)}, split by ${breakdownName}`));
    } else {
        paragraphs.push(sentence(section.displayAs === "percentage"
            ? `Counts ${counted} and shows each ${breakdownName} as a percentage of the total`
            : `Counts ${counted}, split by ${breakdownName}`));
    }

    // The period itself is stated once, up in the scope section - it is report-level, so
    // repeating it under every metric only adds noise. The date field is not: each model
    // can be anchored to a different one.
    const periodDateText = periodDateNarrative(context, report, section);
    if (metricOverridesReportPeriod(section)) {
        const own = metricPeriodFilterOutline(context, report, section);
        paragraphs.push(sentence(own
            ? `The report's reporting period does not apply to this metric; instead it only includes records where ${own}`
            : "The report's reporting period does not apply to this metric, and it has no date criteria of its own - it reads every date"));
        if (periodDateText) paragraphs.push(sentence(`Each record is dated ${periodDateText}`));
    } else if (!modelAppliesReportingPeriod(report, section.model)) {
        paragraphs.push(sentence("The report's reporting period is turned off for this metric's data model, so it reads every date"));
        if (periodDateText) paragraphs.push(sentence(`Each record is dated ${periodDateText}`));
    } else if (periodDateText) {
        paragraphs.push(sentence(`Each record is dated ${periodDateText}`));
        if (section.periodDate?.applyToReportingPeriod) paragraphs.push(sentence("That date, not the report's date field, decides whether a record falls inside the reporting period"));
        else paragraphs.push(sentence(`Whether a record falls inside the reporting period is still decided ${dateWords}`));
    } else {
        paragraphs.push(sentence(`Records are dated ${dateWords}`));
    }

    if (section.groups.length) {
        const groups = section.groups
            .filter((group) => group.values.length)
            .map((group) => `${group.name || "an unnamed group"} (${joinList(valueLabels(context, section.model, section.breakdownColumn, group.values))})`);
        if (groups.length) paragraphs.push(sentence(`Some values are reported together: ${joinList(groups)}. Everything else is counted on its own`));
    }

    if (section.extraBreakdowns.length) {
        const extras = section.extraBreakdowns
            .map((extra) => {
                if (isPeriodDateExtra(extra)) {
                    const part = granularityLabel(extra.granularity);
                    return part ? `${part.toLowerCase()} of that date` : "";
                }
                const column = breakdownColumnForModel(context, section.model, extra.column);
                if (!column) return "";
                const granularity = granularityLabel(extra.granularity);
                return granularity ? `${granularity.toLowerCase()} of ${column.label.toLowerCase()}` : column.label.toLowerCase();
            })
            .filter(Boolean);
        if (extras.length) paragraphs.push(sentence(`It is broken down further by ${joinList(extras)}`));
    }

    const coPlans = section.applyClientFilter === false ? [] : coPlanNames(section, clientOptions);
    if (section.applyClientFilter === false) {
        paragraphs.push(sentence("It is not limited to this report's health plan: the client filter is turned off for this metric, so only its own inclusion filters decide whose records are counted"));
    } else if (coPlans.length) {
        paragraphs.push(sentence(`Unlike the rest of this report, it also counts records belonging to ${joinList(coPlans)}`));
    }

    if (section.include.length) {
        paragraphs.push(sentence(`Only these ${breakdownName} values are counted: ${joinList(valueLabels(context, section.model, section.breakdownColumn, section.include))}`));
    }
    if (section.exclude.length) {
        paragraphs.push(sentence(`These ${breakdownName} values are left out: ${joinList(valueLabels(context, section.model, section.breakdownColumn, section.exclude))}`));
    }

    const includeClause = metricFilterClause(context, report, section, "filter");
    if (includeClause) paragraphs.push(sentence(`It only counts records where ${clauseText(includeClause)}`));
    const excludeClause = metricFilterClause(context, report, section, "excludeFilter");
    if (excludeClause) paragraphs.push(sentence(`It leaves out records where ${clauseText(excludeClause)}`));

    if (section.displayAs === "otp") {
        if (section.otpRules.length) {
            section.otpRules.forEach((rule, index) => paragraphs.push(otpRuleSentence(context, report, section, rule, index)));
        } else {
            paragraphs.push(sentence("No late-arrival rules have been set yet, so nothing can be counted as late"));
        }
        paragraphs.push(sentence(section.otpMissingHandling === "exclude"
            ? "Records missing one of those times are left out of the calculation"
            : section.otpMissingHandling === "late"
                ? "Records missing one of those times are counted as late"
                : "Records missing one of those times are counted as on time"));
    }

    const subtitleParts = [model?.label || "No data model"];
    if (coPlans.length) subtitleParts.push(`${coPlans.length} ${plural(coPlans.length, "co-plan", "co-plans")}`);

    return {
        id: section.id,
        title: name,
        subtitle: subtitleParts.join(" · "),
        paragraphs,
        outline: clauseDepth(includeClause) > 2 ? includeClause : null,
    };
}

// --- categorisation ------------------------------------------------------------------

function ruleSentence(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet, rule: CategorizationRule) {
    const clause = describeConditionTree(context, report, rule.conditions, rule.conditionLogic);
    const target = targetFieldDef(context, rule.targetField, report);
    const value = ruleResultLabel(context, report, rule);
    const condition = clause ? clauseText(clause) : "no conditions have been set";

    if (rule.action === "include") return sentence(`A record is kept only if ${condition}`);
    if (rule.action === "exclude") return sentence(`A record is dropped if ${condition}`);
    if (ruleValueFromColumn(rule)) {
        // Mirrors the SQL: a blank source value does not match, so the next rule gets a turn.
        return sentence(`A record is labelled with ${value || NOT_SET} if ${condition} and that value is not blank`.concat(
            target.virtual ? "" : ` (this sets ${target.label.toLowerCase()})`,
        ));
    }
    return sentence(`A record is called ${value || NOT_SET} if ${condition}`.concat(
        target.virtual ? "" : ` (this sets ${target.label.toLowerCase()})`,
    ));
}

function describeRuleSet(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet): NarrativeBlock {
    const paragraphs: string[] = [];
    const categories = new Set(ruleSet.rules.map((rule) => ruleResultValue(rule)).filter(Boolean));
    const conditionCount = ruleSet.rules.reduce((count, rule) => count + countConditions(rule.conditions), 0);

    if (!ruleSet.rules.length) {
        paragraphs.push(sentence("This rule set has no rules yet, so it sorts nothing"));
    } else {
        // A column-sourced rule produces one category per distinct value, so the count is
        // open-ended and is not stated as a number.
        const openEnded = ruleSet.rules.some((rule) => rule.action === "set" && ruleValueFromColumn(rule));
        paragraphs.push(sentence(openEnded
            ? `${ruleSet.name || "This rule set"} sorts each record into categories, some of them taken from a column's own values. Rules are read in order, and the first one that matches wins`
            : `${ruleSet.name || "This rule set"} sorts each record into ${categories.size || ruleSet.rules.length} ${plural(categories.size || ruleSet.rules.length, "category", "categories")}. Rules are read in order, and the first one that matches wins`));
        ruleSet.rules.forEach((rule) => paragraphs.push(ruleSentence(context, report, ruleSet, rule)));
    }

    paragraphs.push(sentence(ruleSet.includeElse === false
        ? "A record matching none of these rules is left out of the report entirely"
        : `A record matching none of these rules is reported as ${ruleSet.elseLabel.trim() || "its original value"}`));

    return {
        id: ruleSet.id,
        title: ruleSet.name || "Categorisation",
        subtitle: `${ruleSet.rules.length} ${plural(ruleSet.rules.length, "rule", "rules")} · ${conditionCount} ${plural(conditionCount, "condition", "conditions")}`,
        paragraphs,
        outline: null,
    };
}

// --- scope, schedule, exclusions -------------------------------------------------------

function describeScope(
    context: ReportLogicContext,
    plan: PlanConfig,
    report: ReportConfig,
    clientOptions?: ReportConfigurationLookupOption[],
): NarrativeSection {
    const planName = plan.clientName || plan.name || "this health plan";
    const models = report.models.map((modelKey) => context.dataModels[modelKey]?.label || modelKey).filter(Boolean);
    const paragraphs: string[] = [];

    paragraphs.push(sentence(models.length
        ? `This report reads ${joinList(models.map((label) => label.toLowerCase()))} ${plural(models.length, "records", "records")} belonging to ${planName}`
        : `This report has no data attached yet, so it returns nothing`));

    const coPlans = Array.from(new Set(report.sections
        .filter((section) => section.applyClientFilter !== false)
        .flatMap((section) => coPlanNames(section, clientOptions))));
    const unscoped = report.sections.filter((section) => section.applyClientFilter === false).length;
    if (coPlans.length) {
        paragraphs.push(sentence(`Some metrics reach past ${planName} and also count ${joinList(coPlans)}; each one says so below`));
    } else if (!unscoped) {
        paragraphs.push(sentence(`Nothing outside ${planName} is counted`));
    }
    if (unscoped) {
        paragraphs.push(sentence(`${unscoped} ${plural(unscoped, "metric has", "metrics have")} the client filter turned off and ${plural(unscoped, "is", "are")} scoped only by ${plural(unscoped, "its", "their")} own inclusion filters; each one says so below`));
    }
    const periodOff = report.models.filter((modelKey) => !modelAppliesReportingPeriod(report, modelKey))
        .map((modelKey) => context.dataModels[modelKey]?.label || modelKey);
    if (periodOff.length) {
        paragraphs.push(sentence(`The reporting period is turned off for ${joinList(periodOff.map((label) => label.toLowerCase()))}, so those records are read for every date`));
    }

    paragraphs.push(sentence(`Each run covers ${periodPhrase(report)}`));

    return { id: "scope", heading: "What this report covers", paragraphs, blocks: [] };
}

function describeSchedule(report: ReportConfig): NarrativeSection {
    const start = formatReportDate(report.effectiveStart);
    const end = formatReportDate(report.effectiveEnd);
    const paragraphs = [
        sentence(report.frequency ? `It runs ${report.frequency.toLowerCase()}` : "No schedule has been set yet"),
        sentence(start
            ? `It applies from ${start}${end && end !== "Ongoing" ? ` until ${end}` : " onwards, with no end date"}`
            : "No start date has been set"),
    ];
    return { id: "schedule", heading: "When it runs", paragraphs, blocks: [] };
}

function describeExclusions(context: ReportLogicContext, report: ReportConfig): NarrativeSection {
    const filters = reportGlobalFilters(report);
    const paragraphs: string[] = [];

    if (filters.exclusions.memberIds.length) {
        paragraphs.push(sentence(`These members are left out of every metric: ${joinList(filters.exclusions.memberIds)}`));
    }
    if (filters.exclusions.invoiceNumbers.length) {
        paragraphs.push(sentence(`These invoices are left out of every metric: ${joinList(filters.exclusions.invoiceNumbers)}`));
    }
    // "both" is the wider of the two settings and adds no restriction, so it is only worth
    // a sentence when the report is narrowed to active trips.
    if (filters.tripActivity === "active-only") {
        paragraphs.push(sentence("Only active trips are counted"));
    }

    if (report.models.includes("transportation")) {
        (["include", "exclude"] as const).forEach((groupKey) => {
            const group = report.tripDataFilters[groupKey];
            const descriptions = TRIP_DATA_FIELDS.map((field) => {
                const state = group.fields[field.key];
                if (!state) return "";
                const bits: string[] = [];
                if (state.values.length) bits.push(`is one of ${joinList(valueLabels(context, "transportation", field.column || field.key, state.values), "or")}`);
                if (field.hasText && state.freeText.trim()) bits.push(`is "${state.freeText.trim()}"`);
                if (field.hasText && state.contains.trim()) bits.push(`contains "${state.contains.trim()}"`);
                return bits.length ? `${field.label.toLowerCase()} ${joinList(bits, "or")}` : "";
            }).filter(Boolean);
            if (descriptions.length) {
                const joined = joinList(descriptions, group.logic === "or" ? "or" : "and");
                paragraphs.push(sentence(groupKey === "include"
                    ? `Every transportation metric counts only trips where ${joined}`
                    : `Every transportation metric leaves out trips where ${joined}`));
            }
        });
    }

    if (!paragraphs.length) paragraphs.push(sentence("Nothing is excluded at the report level"));

    return { id: "exclusions", heading: "What is left out of every metric", paragraphs, blocks: [] };
}

// --- the rule book ---------------------------------------------------------------------

/**
 * The whole configuration as a document: headings, lead sentences and paragraphs, ready
 * for a renderer to lay out. Everything a reader needs is in the text - the renderer adds
 * no facts of its own.
 */
function buildRuleBook(
    context: ReportLogicContext,
    plan: PlanConfig,
    report?: ReportConfig,
    clientOptions?: ReportConfigurationLookupOption[],
): RuleBook | null {
    if (!report) return null;
    context = contextWithCalculatedColumns(context, report);

    const planName = plan.clientName || plan.name || "Selected health plan";
    const effective = formatReportDate(report.effectiveStart);
    const subtitle = [planName, report.frequency ? `runs ${report.frequency.toLowerCase()}` : "", effective ? `from ${effective}` : ""]
        .filter(Boolean)
        .join(" · ");

    const metrics: NarrativeSection = {
        id: "metrics",
        heading: "What it counts",
        lead: report.sections.length
            ? sentence(`This report produces ${report.sections.length} ${plural(report.sections.length, "figure", "figures")}`)
            : sentence("No metrics have been added yet"),
        paragraphs: [],
        blocks: report.sections.map((section) => describeMetric(context, report, section, clientOptions)),
    };

    const categorisation: NarrativeSection = {
        id: "categorisation",
        heading: "How records are sorted into categories",
        lead: report.bucketRuleSets.length
            ? sentence("A metric can group records into categories of your own instead of the raw column values. These are the rules that decide which category a record falls into")
            : sentence("No categorisation rules are configured, so metrics report the raw column values"),
        paragraphs: [],
        blocks: report.bucketRuleSets.map((ruleSet) => describeRuleSet(context, report, ruleSet)),
    };

    const fields = calculatedFields(report);
    const calculated: NarrativeSection = {
        id: "calculated",
        heading: "Calculated fields",
        lead: sentence("Numbers worked out for each record from its other values. Multiplication, division and remainder are applied before addition and subtraction, and a blank value or a division by zero gives a blank result unless stated otherwise"),
        paragraphs: [],
        blocks: fields.map((field) => ({
            id: field.id,
            title: field.name || "Untitled calculated field",
            subtitle: context.dataModels[field.model]?.label || "No data model chosen",
            paragraphs: [sentence(`${field.name || "This field"} = ${calculatedFieldEnglish(context, field) || "not finished yet"}`)],
            outline: null,
        })),
    };

    return {
        title: report.label || "Report",
        subtitle,
        sections: [
            describeScope(context, plan, report, clientOptions),
            describeSchedule(report),
            metrics,
            categorisation,
            ...(fields.length ? [calculated] : []),
            describeExclusions(context, report),
        ],
    };
}

/**
 * The rule book as flat text, for the plainEnglishText carried in the saved configuration.
 * Same words the reader sees on screen - that is the point of it going through here.
 */
function ruleBookPlainText(book: RuleBook | null) {
    if (!book) return "";
    const lines = [book.title, book.subtitle, ""];
    book.sections.forEach((section) => {
        lines.push(section.heading.toUpperCase());
        if (section.lead) lines.push(section.lead);
        section.paragraphs.forEach((paragraph) => lines.push(paragraph));
        section.blocks.forEach((block) => {
            lines.push("");
            lines.push(block.subtitle ? `${block.title} (${block.subtitle})` : block.title);
            block.paragraphs.forEach((paragraph) => lines.push(`  ${paragraph}`));
        });
        lines.push("");
    });
    return lines.join("\n").trim();
}

export {
    joinList,
    sentence,
    valueLabel,
    valueLabels,
    describeCondition,
    describeConditionTree,
    clauseText,
    clauseDepth,
    describeMetric,
    describeRuleSet,
    describeScope,
    describeSchedule,
    describeExclusions,
    buildRuleBook,
    ruleBookPlainText,
};
